import { t as translate } from "@colorglo/i18n/catalogs/staff";
import { useTheme } from "@colorglo/ui";
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
} from "@colorglo/ui/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useSignIn } from "../api/auth.ts";
import { useT } from "../lib/locale.ts";

export const Route = createFileRoute("/logga-in")({ component: SignInPage });

/**
 * shadcn/ui `login-02`: the form on the left, a full-bleed photograph on the right that is
 * dropped below `lg`.
 *
 * The image is the client's to supply — until it lands this renders the brand cyan rather
 * than a broken `<img>`, so the page is shippable now and the swap is one `src`.
 */
function SignInPage() {
  const t = useT();
  const navigate = useNavigate();
  const signIn = useSignIn();
  const theme = useTheme();

  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <img
            src={theme === "dark" ? "/logo/colorglo-logo-light.svg" : "/logo/colorglo.svg"}
            alt={translate("app.title")}
            className="h-12 w-auto"
            width={2643}
            height={1011}
          />
        </div>

        <div className="flex flex-1 items-center justify-center">
          <form
            className="w-full max-w-sm"
            onSubmit={(event) => {
              event.preventDefault();
              signIn.mutate({ email, password }, { onSuccess: () => void navigate({ to: "/" }) });
            }}
          >
            <FieldGroup>
              <div className="flex flex-col items-center gap-1 text-center">
                <h1 className="text-h1">{t("auth.sign_in.title")}</h1>
                <p className="text-body-sm text-balance text-muted-foreground">
                  {t("auth.sign_in.body")}
                </p>
              </div>

              <Field>
                <FieldLabel htmlFor={emailId}>{t("auth.sign_in.email")}</FieldLabel>
                <Input
                  id={emailId}
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  {...(signIn.isError ? { "aria-invalid": true, "aria-describedby": errorId } : {})}
                />
              </Field>

              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor={passwordId}>{t("auth.sign_in.password")}</FieldLabel>
                  <a
                    href="mailto:support@colorglo.se"
                    className="ml-auto text-body-sm underline-offset-4 hover:underline"
                  >
                    {t("auth.sign_in.forgot")}
                  </a>
                </div>
                <Input
                  id={passwordId}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  {...(signIn.isError ? { "aria-invalid": true, "aria-describedby": errorId } : {})}
                />
                {signIn.isError && <FieldError id={errorId}>{t("auth.sign_in.failed")}</FieldError>}
              </Field>

              <Field>
                <Button type="submit" disabled={signIn.isPending}>
                  {signIn.isPending ? t("auth.sign_in.working") : t("auth.sign_in.submit")}
                </Button>
                <FieldDescription className="text-center">
                  {t("auth.sign_in.no_account")}
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </div>
      </div>

      <div className="relative hidden bg-brand-subtle lg:block">
        <img
          src="/login.jpg"
          alt={t("auth.sign_in.image_alt")}
          className="absolute inset-0 h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.hidden = true;
          }}
        />
      </div>
    </div>
  );
}
